import m from '@bitstillery/mithril'
import {$s} from '@bitstillery/common/app'
import {cached_media_folder_files, load_media_folder_files, media_folder_file_src} from '@bitstillery/common/cms/lib/media'
import {present, text_before} from '@bitstillery/common/lib/utils'

/** The shape `$s.support.contact` is rendered from — see the support module, panel cart and CMS footer. */
export interface SupportContact {
    email?: string | undefined
    img?: string | undefined
    mobile?: string | undefined
    name: string
    phone?: string | undefined
    role?: string
}

// A role is stored as the key itself and rendered with `$t(contact.role)`, so nothing here reads as a
// call site. The whole map is declared at once — covering one entry left its two siblings looking dead.
// @i18n ignore: support.roles.*
const roles = {
    sales: 'support.roles.sales_nl',
    support: 'support.roles.default',
    tech: 'support.roles.tech',
}

// Faces shown to *anonymous* visitors — a logged-in portal user always gets their own support manager
// from the profile API instead. Keys match the local part of the employee's address.
const people = {
    collin: {
        email: 'collin@springtimegroup.nl',
        name: 'Collin van Dijken',
        role: roles.tech,
    },
    cor: {
        name: 'Cor Nuijten',
    },
    gianfranco: {
        name: 'Gian Franco Etzi',
    },
    natalia: {
        name: 'Natalia Pleguezuelo',
    },
    jeroen: {
        email: 'jeroen@springtimegroup.nl',
        name: 'Jeroen van Veen',
        role: roles.tech,
    },
    klaasjan: {
        email: 'klaasjan@springtimegroup.nl',
        name: 'KlaasJan Elzinga',
        role: roles.tech,
    },
    patrick: {
        name: 'Patrick Sinning',
    },
    rene: {
        name: 'René Dolfing',
    },
    tarek: {
        name: 'Tarek Chokair',
    },
}

// Employee portraits in the shared CMS media library, one `<local part of their address>.jpg` per
// employee. Staff add a face by uploading a file there, not by editing this module.
const EMPLOYEE_FOLDER = 'employee'

const DISCOVER_SUPPORT_PHONE = '+31 50 211 09 09'

const support_team_tech = {
    collin: people.collin,
    jeroen: people.jeroen,
    klaasjan: people.klaasjan,
    unknown: {},
}

const tech_colleagues = Object.values(support_team_tech)
support_team_tech.unknown = present(tech_colleagues[Math.floor(Math.random() * tech_colleagues.length)], 'tech colleague')

/** A roster by colleague key. */
type SupportTeam = Record<string, Partial<SupportContact>>

/** Each package's roster. A type alias rather than an interface, so get_support can look one up by package name. */
type SupportTeams = {discover: SupportTeam; portal: SupportTeam; sales: SupportTeam}

const team_a2bc_etr: SupportTeams = {
    discover: support_team_tech,
    portal: {
        gianfranco: people.gianfranco,
        rene: people.rene,
    },
    sales: {
        gianfranco: {...people.gianfranco, role: roles.sales},
        rene: {...people.rene, role: roles.sales},
    },
}

const team_msi_msp: SupportTeams = {
    discover: support_team_tech,
    portal: {
        cor: people.cor,
        gianfranco: people.gianfranco,
        patrick: people.patrick,
        rene: people.rene,
        tarek: people.tarek,
        natalia: people.natalia,
    },
    sales: {
        gianfranco: {...people.gianfranco, role: roles.sales},
    },
}

/**
 * Represents the structure and operations on the team,
 * including team members and their details.
 */
export const team = {
    a2bc: team_a2bc_etr,
    etr: team_a2bc_etr,
    msi: team_msi_msp,
    msp: team_msi_msp,
}

/**
 * Fetch the portraits before the first `get_support()` — it reads them synchronously, so an app that
 * boots without awaiting this renders its support contact faceless and never revisits it.
 */
export function load_support_portraits(): Promise<unknown> {
    return load_media_folder_files(EMPLOYEE_FOLDER, 'shared')
}

function portrait(key: string): string | undefined {
    const name = key.toLowerCase()
    const file = cached_media_folder_files(EMPLOYEE_FOLDER, 'shared').find(
        (f) => f.name.replace(/\.[^.]+$/, '').toLowerCase() === name,
    )
    return file ? media_folder_file_src(file) : undefined
}

/** Contact details of the account itself, shown before we know who the visitor is. */
function account_contact_details() {
    if (['msi', 'msp'].includes(process.env.MSI_THEME || '')) {
        return {email: 'info@movingspirits.nl', phone: DISCOVER_SUPPORT_PHONE}
    }
    return {email: 'info@a2bc.com', phone: '+31 50 211 186 1'}
}

/**
 * Turns a roster entry into a contact. Returns a new object every call: the roster is module state
 * shared by every SSR request, so filling gaps in place would leak one visitor's details into the next.
 */
function roster_contact(key: string, entry: Partial<SupportContact>, defaults: Partial<SupportContact>): SupportContact {
    const merged = {...defaults, ...entry}
    // `unknown` in the Discover schedule is a copy of a colleague, so the roster key isn't theirs — but
    // their own address is. The account-generic address from `defaults` must never be used here.
    const portrait_key = entry.email?.split('@')[0] || key
    return {
        ...merged,
        img: merged.img || portrait(portrait_key),
        name: merged.name || 'Support',
        role: merged.role || roles.support,
    }
}

export function get_support(): SupportContact {
    const teams_by_theme: Partial<Record<string, SupportTeams>> = team
    const teams = present(teams_by_theme[process.env.MSI_THEME || 'a2bc'], 'support teams for the theme')

    if (process.env.MSI_PACKAGE === 'discover') {
        const support_schedule: Record<number, string> = {
            0: 'unknown',
            1: 'klaasjan',
            2: 'klaasjan',
            3: 'jeroen',
            4: 'collin',
            5: 'collin',
            6: 'unknown',
        }

        const day = new Date().getDay()
        const key = support_schedule[day] || 'unknown'
        const entry = teams.discover[key] || support_team_tech.unknown
        return roster_contact(key, entry, {phone: DISCOVER_SUPPORT_PHONE})
    } else if (process.env.MSI_PACKAGE === 'portal') {
        const manager = $s.support.manager
        if (manager.name) {
            // The profile API is authoritative about who the support manager is. Matching its address
            // against the roster used to silently fall back to the first colleague in the list, pairing
            // one person's name and face with another's mailto.
            return {
                email: manager.email_address || undefined,
                img: portrait(text_before(manager.email_address, '@')),
                mobile: manager.mobile_telephone_number || undefined,
                name: manager.name,
                phone: manager.phone_number || undefined,
                role: roles.support,
            }
        }

        // No profile yet, so no manager to name: show a colleague from the roster with the account's
        // own contact details.
        const colleagues = Object.entries(teams.portal)
        const [key, entry] = present(colleagues[(colleagues.length * Math.random()) << 0], 'support colleague')
        return roster_contact(key, entry, account_contact_details())
    }

    const teams_by_package: Partial<Record<string, SupportTeam>> = teams
    const [fallback] = Object.entries(present(teams_by_package[process.env.MSI_PACKAGE || 'discover'], 'support team'))
    if (fallback) {
        return roster_contact(fallback[0], fallback[1], account_contact_details())
    }
    return {name: 'Support', role: roles.support, ...account_contact_details()}
}
